<template>
    <slide-transition>
        <section v-if="isVisible && currentNotification" class="stf-notification-banner" :class="{ 'stf-notification-banner-error': currentNotification.type === 'error' }">
            <StfContainer>
                <div class="stf-notification-banner-wrapper">
                    <div class="stf-notification-banner-left">
                        <div class="stf-notification-banner-content">
                            <span v-html="currentNotification.message"></span>
                        </div>
                    </div>
                    <button
                        class="stf-notification-banner-close"
                        aria-label="Close notification"
                        @click="closeBanner"
                    >
                        <StfIcon icon="xMark" size="small" color="black" />
                    </button>
                </div>
            </StfContainer>
        </section>
    </slide-transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import StfIcon from '@/ui/icon/StfIcon.vue';
import SlideTransition from '@/transitions/slide-transition.vue';
import { useNotification, type Notification } from '@/composables/useNotification';
const { notificationBus } = useNotification();

const currentNotification = ref<Notification | null>(null);
const isVisible = ref(false);
let isProcessingNotification = false;

const closeBanner = () => {
    isVisible.value = false;
};

const handleUserInteraction = () => {
    if (!isProcessingNotification && isVisible.value) {
        isVisible.value = false;
    }
};

// As there is an issue with the same click triggering the notification and then closing it, 
// we need to set a flag to prevent the notification from closing.
const handleNotificationShown = (notification: Notification) => {
    isProcessingNotification = true;
    currentNotification.value = notification;
    isVisible.value = true;
    setTimeout(() => {
        isProcessingNotification = false;
    }, 500);
};

onMounted(() => {
    // Add event listeners
    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    notificationBus.on(handleNotificationShown);
});

onUnmounted(() => {
    // Remove event listeners
    document.removeEventListener('click', handleUserInteraction);
    document.removeEventListener('keydown', handleUserInteraction);
    notificationBus.off(handleNotificationShown);
});
</script>

<style lang="scss">
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

.stf-notification-banner {
    width: 100%;
    background-color: vars.$color-info;

    &-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 36px;
        position: relative;
        
        @include mixins.mobile {
            padding: 15px 10px 15px 0px;
        }
    }

    &-left {
        display: flex;
        align-items: center;
        padding-left: 5px;
        padding-right: 10px;
    }

    &-content {
        text-align: left;
    }

    &-close {
        display: flex;
        align-items: flex-start;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
    }

    &-error {
        background-color: vars.$color-error-light;
    }
}
</style> 